/**
 * useTheme hook - Theme management with localStorage persistence
 *
 * Supports light, dark, and system themes with automatic system preference detection.
 * Applies theme by adding/removing 'dark' class on document.documentElement and
 * setting data-theme attribute for CSS variable support.
 *
 * @example
 * const { theme, setTheme, resolvedTheme, toggle } = useTheme();
 *
 * // Get current theme setting ('light', 'dark', or 'system')
 * console.log(theme);
 *
 * // Get the actual applied theme ('light' or 'dark')
 * console.log(resolvedTheme);
 *
 * // Set theme
 * setTheme('dark');
 * setTheme('system');
 *
 * // Toggle between light and dark
 * toggle();
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

const THEME_STORAGE_KEY = 'theme';
const VALID_THEMES = ['light', 'dark', 'system'];

/**
 * Get the system's preferred color scheme
 * @returns {'light' | 'dark'}
 */
function getSystemTheme() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Get stored theme from localStorage or default to 'light'
 * @returns {'light' | 'dark' | 'system'}
 */
function getStoredTheme() {
  if (typeof window === 'undefined') return 'light';

  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && VALID_THEMES.includes(stored)) {
      return stored;
    }
  } catch (e) {
    // localStorage may not be available (SSR, private browsing, etc.)
    console.warn('Failed to read theme from localStorage:', e);
  }

  return 'light';
}

/**
 * Apply theme to the document
 * @param {'light' | 'dark'} resolvedTheme - The actual theme to apply
 */
function applyTheme(resolvedTheme) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;

  if (resolvedTheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  root.setAttribute('data-theme', resolvedTheme);
}

/**
 * Theme management hook
 *
 * @returns {{
 *   theme: 'light' | 'dark' | 'system',
 *   setTheme: (theme: 'light' | 'dark' | 'system') => void,
 *   resolvedTheme: 'light' | 'dark',
 *   toggle: () => void
 * }}
 */
export function useTheme() {
  // Initialize theme from localStorage
  const [theme, setThemeState] = useState(() => getStoredTheme());

  // Track system preference for 'system' theme
  const [systemTheme, setSystemTheme] = useState(() => getSystemTheme());

  // Compute the resolved theme (what's actually applied)
  const resolvedTheme = useMemo(() => {
    if (theme === 'system') {
      return systemTheme;
    }
    return theme;
  }, [theme, systemTheme]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Legacy browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Apply theme whenever resolvedTheme changes
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Apply theme on initial mount (before React hydration)
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set theme and persist to localStorage
  const setTheme = useCallback((newTheme) => {
    if (!VALID_THEMES.includes(newTheme)) {
      console.warn(`Invalid theme: ${newTheme}. Valid themes are: ${VALID_THEMES.join(', ')}`);
      return;
    }

    setThemeState(newTheme);

    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch (e) {
      console.warn('Failed to save theme to localStorage:', e);
    }
  }, []);

  // Toggle between light and dark (uses resolved theme for system)
  const toggle = useCallback(() => {
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  return {
    theme,
    setTheme,
    resolvedTheme,
    toggle,
  };
}

export default useTheme;
